import { c as _c } from "react/compiler-runtime";
import React, { useMemo, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FixedButton } from 'record-it-components/button/FixedButton/FixedButton';
import { DateTimeInput, DateTimeInputProps } from 'record-it-components/dateTime/DateTimeInput/DateTimeInput';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useNotificationManager } from 'record-it-components/hooks/useNotificationManager';
import { TextInput, TextInputProps } from 'record-it-components/input/TextInput/TextInput';
import { MessageBox, MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { WorkTimeEntryUtils } from '../../../classes/entities/WorkTimeEntry/WorkTimeEntryUtils';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { validateWorkTimeEntry } from '../../../hooks/validation/functions/validateWorkTimeEntry';
import { useValidation } from '../../../hooks/validation/useValidation';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import { EditWorkTimeEntryBreaksWidget, EditWorkTimeEntryBreaksWidgetProps } from '../../timeTracking/EditWorkTimeEntryBreaksWidget/EditWorkTimeEntryBreaksWidget';
import { WorkTimeEntryToCreateActivityWidget } from '../../workTime/WorkTimeEntryToCreateActivityWidget/WorkTimeEntryToCreateActivityWidget';
import { WorkTimeEntryToCreateConstructionSiteWidget } from '../../workTime/WorkTimeEntryToCreateConstructionSiteWidget/WorkTimeEntryToCreateConstructionSiteWidget';
import styles from './CreateWorkTimeEntryPage.module.css';
export type CreateWorkTimeEntryPageProps = PropsWithoutChildren<Record<string, never>>;

/**
 * this Page can be put into routes with either the stopwatchId or the userGroupId param
 */
export const CreateWorkTimeEntryPage: FunctionComponent<CreateWorkTimeEntryPageProps> = React.memo(props => {
  const $ = _c(95);
  prepareProps({
    props,
    ignoreChildren: true
  });
  useConfigureBreadcrumbsWithBuilder(_temp);
  const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
  let t0;
  if ($[0] !== workTimeEntryToCreate) {
    t0 = validateWorkTimeEntry(workTimeEntryToCreate);
    $[0] = workTimeEntryToCreate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== workTimeEntryToCreate) {
    t1 = [workTimeEntryToCreate];
    $[2] = workTimeEntryToCreate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const validationResult = useValidation(t0, t1);
  const dispatch = useAppDispatch();
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const notificationManager = useNotificationManager();
  const {
    getRequiredCurrentUser
  } = useCurrentUser();
  const stopwatchManager = useStopwatchManager();
  let t2;
  if ($[4] !== workTimeEntryToCreate.startDate) {
    t2 = new Date(workTimeEntryToCreate.startDate);
    $[4] = workTimeEntryToCreate.startDate;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const newBreakInitialStartDate = t2;
  let t3;
  if ($[6] !== workTimeEntryToCreate.endDate) {
    t3 = new Date(workTimeEntryToCreate.endDate);
    $[6] = workTimeEntryToCreate.endDate;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const newBreakInitialEndDate = t3;
  let t4;
  if ($[8] !== dispatch) {
    t4 = newDate => {
      if (newDate) {
        dispatch(createWorkTimeEntrySlice.actions.setStartDate(newDate.toISOString()));
      }
    };
    $[8] = dispatch;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleStartDateInput = useEventCallback(t4);
  let t5;
  if ($[10] !== dispatch) {
    t5 = newDate_0 => {
      if (newDate_0) {
        dispatch(createWorkTimeEntrySlice.actions.setEndDate(newDate_0.toISOString()));
      }
    };
    $[10] = dispatch;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleEndDateInput = useEventCallback(t5);
  let t6;
  if ($[12] !== dispatch) {
    t6 = newNote => {
      dispatch(createWorkTimeEntrySlice.actions.setNote(newNote));
    };
    $[12] = dispatch;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleNoteInput = useEventCallback(t6);
  let t7;
  if ($[14] !== dispatch) {
    t7 = breaks => {
      dispatch(createWorkTimeEntrySlice.actions.setBreaks(breaks));
    };
    $[14] = dispatch;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleBreaksModified = useEventCallback(t7);
  const workTimeEntryRepository = useEntityRepository(EntityName.WorkTimeEntry);
  let t8;
  if ($[16] !== getRequiredCurrentUser || $[17] !== navigate || $[18] !== notificationManager || $[19] !== stopwatchManager || $[20] !== t || $[21] !== workTimeEntryRepository || $[22] !== workTimeEntryToCreate.breaks || $[23] !== workTimeEntryToCreate.constructionSiteId || $[24] !== workTimeEntryToCreate.endDate || $[25] !== workTimeEntryToCreate.note || $[26] !== workTimeEntryToCreate.startDate || $[27] !== workTimeEntryToCreate.stopwatchId || $[28] !== workTimeEntryToCreate.timeTrackingActivityId || $[29] !== workTimeEntryToCreate.userGroupId) {
    t8 = () => {
      workTimeEntryRepository.create({
        userId: getRequiredCurrentUser().id,
        constructionSiteId: workTimeEntryToCreate.constructionSiteId,
        timeTrackingActivityId: workTimeEntryToCreate.timeTrackingActivityId,
        startDate: workTimeEntryToCreate.startDate,
        endDate: workTimeEntryToCreate.endDate,
        note: workTimeEntryToCreate.note,
        breaks: workTimeEntryToCreate.breaks,
        ownerUserGroupId: workTimeEntryToCreate.userGroupId
      });
      if (workTimeEntryToCreate.stopwatchId != null) {
        stopwatchManager.remove(workTimeEntryToCreate.stopwatchId);
      }
      navigate(RoutePaths.home());
      notificationManager.success({
        title: t("components.page.CreateWorkTimeEntryPage.createdNotification.title"),
        message: t("components.page.CreateWorkTimeEntryPage.createdNotification.message")
      });
    };
    $[16] = getRequiredCurrentUser;
    $[17] = navigate;
    $[18] = notificationManager;
    $[19] = stopwatchManager;
    $[20] = t;
    $[21] = workTimeEntryRepository;
    $[22] = workTimeEntryToCreate.breaks;
    $[23] = workTimeEntryToCreate.constructionSiteId;
    $[24] = workTimeEntryToCreate.endDate;
    $[25] = workTimeEntryToCreate.note;
    $[26] = workTimeEntryToCreate.startDate;
    $[27] = workTimeEntryToCreate.stopwatchId;
    $[28] = workTimeEntryToCreate.timeTrackingActivityId;
    $[29] = workTimeEntryToCreate.userGroupId;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const handleCreateClick = useEventCallback(t8);
  let t9;
  if ($[31] !== workTimeEntryToCreate.breaks || $[32] !== workTimeEntryToCreate.endDate || $[33] !== workTimeEntryToCreate.startDate) {
    t9 = DurationUtils.formatDurationToHourMinuteString(WorkTimeEntryUtils.getDuration(workTimeEntryToCreate.startDate, workTimeEntryToCreate.endDate, workTimeEntryToCreate.breaks));
    $[31] = workTimeEntryToCreate.breaks;
    $[32] = workTimeEntryToCreate.endDate;
    $[33] = workTimeEntryToCreate.startDate;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const formattedWorkTime = t9;
  let t10;
  if ($[35] !== t) {
    t10 = t("components.page.CreateWorkTimeEntryPage.title");
    $[35] = t;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = <PageContentHeader title={t10} iconConfig={iconConfigurations.prefixed.page.CreateWorkTimeEntryPage.titleIcon} />;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== validationResult.errors || $[40] !== validationResult.valid) {
    t12 = !validationResult.valid ? <MessageBox type={MessageBoxType.Error} className={styles.ErrorMessageBox} data-testid="CreateWorkTimeEntryPage.ErrorMessageBox">{validationResult.errors.map(_temp2)}</MessageBox> : undefined;
    $[39] = validationResult.errors;
    $[40] = validationResult.valid;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t) {
    t13 = t("components.page.CreateWorkTimeEntryPage.startDate");
    $[42] = t;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== workTimeEntryToCreate.startDate) {
    t14 = new Date(workTimeEntryToCreate.startDate);
    $[44] = workTimeEntryToCreate.startDate;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== handleStartDateInput || $[47] !== t13 || $[48] !== t14) {
    t15 = <FormGroup><DateTimeInput label={t13} value={t14} onInput={handleStartDateInput} data-testid="CreateWorkTimeEntryPage.StartDate" /></FormGroup>;
    $[46] = handleStartDateInput;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t) {
    t16 = t("components.page.CreateWorkTimeEntryPage.endDate");
    $[50] = t;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== workTimeEntryToCreate.endDate) {
    t17 = new Date(workTimeEntryToCreate.endDate);
    $[52] = workTimeEntryToCreate.endDate;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== handleEndDateInput || $[55] !== t16 || $[56] !== t17) {
    t18 = <FormGroup><DateTimeInput label={t16} value={t17} onInput={handleEndDateInput} data-testid="CreateWorkTimeEntryPage.EndDate" /></FormGroup>;
    $[54] = handleEndDateInput;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FormGroup><WorkTimeEntryToCreateConstructionSiteWidget /></FormGroup>;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FormGroup><WorkTimeEntryToCreateActivityWidget /></FormGroup>;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== handleBreaksModified || $[61] !== newBreakInitialEndDate || $[62] !== newBreakInitialStartDate || $[63] !== workTimeEntryToCreate.breaks) {
    t21 = <FormGroup><EditWorkTimeEntryBreaksWidget workTimeEntryBreaks={workTimeEntryToCreate.breaks} onWorkTimeEntryBreaksModified={handleBreaksModified} newBreakInitialStartDate={newBreakInitialStartDate} newBreakInitialEndDate={newBreakInitialEndDate} data-testid="CreateWorkTimeEntryPage.TimeEntryBreaksWidget" /></FormGroup>;
    $[60] = handleBreaksModified;
    $[61] = newBreakInitialEndDate;
    $[62] = newBreakInitialStartDate;
    $[63] = workTimeEntryToCreate.breaks;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t) {
    t22 = t("components.page.CreateWorkTimeEntryPage.workTime");
    $[65] = t;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t22) {
    t23 = <span>{t22}</span>;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== formattedWorkTime) {
    t24 = <span data-testid="CreateWorkTimeEntryPage.WorkTimeSummaryValue">{formattedWorkTime}</span>;
    $[69] = formattedWorkTime;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== t23 || $[72] !== t24) {
    t25 = <FormGroup>{t23}{t24}</FormGroup>;
    $[71] = t23;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t) {
    t26 = t("components.page.CreateWorkTimeEntryPage.note");
    $[74] = t;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== handleNoteInput || $[77] !== t26 || $[78] !== workTimeEntryToCreate.note) {
    t27 = <FormGroup><TextInput label={t26} value={workTimeEntryToCreate.note} onInput={handleNoteInput} /></FormGroup>;
    $[76] = handleNoteInput;
    $[77] = t26;
    $[78] = workTimeEntryToCreate.note;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  const t28 = !validationResult.valid;
  let t29;
  if ($[80] !== t) {
    t29 = t("components.page.CreateWorkTimeEntryPage.create");
    $[80] = t;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== handleCreateClick || $[83] !== t28 || $[84] !== t29) {
    t30 = <FixedButton onClick={handleCreateClick} disabled={t28} data-testid="CreateWorkTimeEntryPage.CreateButton">{t29}</FixedButton>;
    $[82] = handleCreateClick;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== t11 || $[87] !== t12 || $[88] !== t15 || $[89] !== t18 || $[90] !== t21 || $[91] !== t25 || $[92] !== t27 || $[93] !== t30) {
    t31 = <PageContentContainer>{t11}{t12}{t15}{t18}{t19}{t20}{t21}{t25}{t27}{t30}</PageContentContainer>;
    $[86] = t11;
    $[87] = t12;
    $[88] = t15;
    $[89] = t18;
    $[90] = t21;
    $[91] = t25;
    $[92] = t27;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  return t31;
});
function _temp(builder) {
  return builder.addHomePage().addCreateWorkTimeEntryPage();
}
function _temp2(errorText) {
  return <div key={errorText}>{errorText}</div>;
}